import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { IRoute, IPageable, IEventFilters } from 'models';
import { eventStore } from 'store';
import { Button, ErrorRequest, Header, Icons, Input, Layout } from 'components/layout';
import { EventsTable } from 'components/event';

import './events.scss';
import { useNavigate } from 'react-router-dom';
import strings from 'tools';
import config from 'config';
import dayjs, { Dayjs } from 'dayjs';

interface Props {
    navigation: IRoute[];
}

const EVENT_TEXT_SEARCH_KEYS = ['name', 'organizer'];

const Events: FC<Props> = observer((props: Props) => {
    const { events, isLoadingEvent, errorEvent, successAdd } = eventStore;
    const { navigation } = props;
    const [filter, setFilter] = useState<IEventFilters>({});
    const [isAddEvent, setIsAddEvent] = useState<boolean>(false);
    const [pageable, setPageable] = useState<IPageable>({ page: 0, size: 10 });
    const [search, setSearch] = useState<string>('');
    const [searchColumn, setSearchColumn] = useState<string>(
        config.search.events[0]?.value || 'name'
    );
    const [statusFilter, setStatusFilter] = useState<string[] | string>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        eventStore.getEvents(pageable, filter);
    }, [pageable, filter]);

    useEffect(() => {
        if (successAdd) {
            setIsAddEvent(false);
            setPageable({ page: 0, size: 10 });
            eventStore.setSuccessAdd(false);
        }
    }, [successAdd]);

    useEffect(() => {
        if (isAddEvent) {
            navigate('/event/new/');
        }
    }, [isAddEvent]);

    const resetPage = () => {
        setPageable({ page: 0, size: 10 });
    };

    const changeFilter = (column: string, searchData: string) => {
        if (column === 'status') {
            setStatusFilter(searchData);
            setFilter((prev) => ({ ...prev, status: searchData }));
            resetPage();
        }
    };

    const onSearch = () => {
        setFilter((prev) => {
            const next = { ...prev };
            EVENT_TEXT_SEARCH_KEYS.forEach((key) => {
                delete (next as Record<string, unknown>)[key];
            });
            if (search.trim() && searchColumn && !searchColumn.includes('datetime')) {
                (next as Record<string, string>)[searchColumn] = search.trim();
            }
            return next;
        });
        resetPage();
    };

    const changeSearch = (column: string, searchText: string) => {
        setSearchColumn(column);
        setSearch(searchText);
    };

    const changeDateSearch = (column: string, dateSearch: Dayjs[]) => {
        const dateType = column === 'datetimeStartRange' ? 'Start' : 'End';
        if (!dateSearch) {
            setFilter((prev) => {
                const newFilter = { ...prev };
                delete newFilter.dateStartFrom;
                delete newFilter.dateStartTo;
                delete newFilter.dateEndFrom;
                delete newFilter.dateEndTo;
                return newFilter;
            });
            resetPage();
        } else if (Array.isArray(dateSearch) && dateSearch[0] === null && dateSearch[1] === null) {
            setFilter((prev) => {
                const newFilter = { ...prev };
                delete newFilter.dateStartFrom;
                delete newFilter.dateStartTo;
                delete newFilter.dateEndFrom;
                delete newFilter.dateEndTo;
                return newFilter;
            });
            resetPage();
        } else {
            const dateFrom = dayjs(dateSearch[0]).format(config.dates.calendar);
            const dateTo = dayjs(dateSearch[1]).format(config.dates.calendar);

            setFilter((prev) => ({
                ...prev,
                [`date${dateType}From`]: dateFrom,
                [`date${dateType}To`]: dateTo,
            }));
            resetPage();
        }
    };

    const changePage = (pageNum: number) => {
        setPageable((prev) => ({ ...prev, page: pageNum }));
    };

    return (
        <Layout page="events" navigation={navigation} error={errorEvent} errorScreen>
            <Header
                title="Мероприятия"
                description={
                    events
                        ? `Всего: ${events.totalElements} 
                        ${strings.ending(events.totalElements, [
                            'мероприятие',
                            'мероприятия',
                            'мероприятий',
                        ])}
                        `
                        : ''
                }
            />
            <div className="page-events">
                <div className="events-top">
                    <Input
                        ref={inputRef}
                        className="events-search"
                        onChange={changeSearch}
                        value={search}
                        type="searchWithSelect"
                        selectOptions={config.search.events}
                        onSearch={onSearch}
                        changeDateSearch={changeDateSearch}
                    />

                    <Button onClick={() => setIsAddEvent(true)} type="primary">
                        {Icons.plus()}Создать мероприятие
                    </Button>
                </div>

                <EventsTable
                    statusFilter={statusFilter}
                    data={events?.content}
                    page={pageable.page}
                    pageSize={pageable.size}
                    onChangePage={changePage}
                    onChangeFilter={changeFilter}
                    total={events?.totalElements}
                    loading={isLoadingEvent}
                />

                {!events && errorEvent && (
                    <ErrorRequest onClick={() => eventStore.getEvents(pageable, filter)} />
                )}
            </div>
        </Layout>
    );
});

export default Events;
